import { useEffect, useState } from "react";

// ─── The entrance ───────────────────────────────────────────────────────────────
//
// ONE CLOCK PER SCREEN, SHARED BY EVERYTHING ON IT.
//
// Every number, bar, wedge and meter is drawn from the same 0→1 progress rather than from its own
// CSS transition. Two reasons. A count that finishes before the bar beneath it reads as two
// unrelated animations rather than one screen arriving; and CSS cannot transition the geometry these
// charts are built from — an SVG bar's height, a donut's arc length — without either per-browser
// caveats or a second mechanism bolted alongside the first.
//
// The cost is a re-render per frame for the length of the run. That is affordable on the screens
// that use it: their figures are already memoised, and they redraw only when someone loads or
// refreshes them. It would NOT be affordable on a screen that re-renders while the clock is running.

/**
 * How long the entrance takes.
 *
 * Deliberately unhurried. The first pass ran in 950ms and the figures arrived faster than they could
 * be read — the counting was over before the eye had settled on a tile. At this length a number is
 * legible while it climbs, which is the only reason to animate one at all.
 *
 * The last item in a staggered row starts at STAGGER_CAP of this, so the whole row finishes by the
 * end of it.
 */
export const INTRO_MS = 2100;

/** Cubic ease-out: quick to start, settling rather than stopping. */
export function ease(p: number): number {
  return 1 - Math.pow(1 - p, 3);
}

/**
 * Raw 0→1 across the entrance, restarted whenever `key` changes — so a refresh replays it rather
 * than snapping the new figures into place.
 *
 * Pass something that changes identity only when the DATA changes: the loaded array, not an object
 * rebuilt on every render, which would restart the run forever.
 *
 * NO prefers-reduced-motion CHECK — deliberately, after it cost an evening.
 *
 * This hook was extracted from the Dashboard, and the extraction added a reduced-motion guard that
 * jumped straight to the finished state. The inline version it replaced had never asked. The result
 * was an entrance that had worked the day before and was silent the next, on a machine whose Windows
 * animation setting was plainly ON — `matchMedia` reported `reduce` anyway, and nothing in Windows
 * Settings would change that. Two screens went quiet at once and the cause looked like anything but
 * a four-line addition to a file that had just been created.
 *
 * If this comes back, it comes back as a SHORTER run rather than no run, and with a way to see what
 * the query is reporting — a silent, invisible, machine-specific kill switch on the one feature
 * whose whole purpose is to be seen is not worth what it costs to diagnose.
 */
export function useIntro(key: unknown, durationMs: number = INTRO_MS): number {
  const [t, setT] = useState(0);

  useEffect(() => {
    let raf = 0;
    const started = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - started) / durationMs);
      setT(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setT(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [key, durationMs]);

  return t;
}

/**
 * A key that changes when the DATA changes, and not merely when it is fetched again.
 *
 * `useIntro` restarts whenever its key changes identity, and a freshly-parsed array is always a new
 * object even when every row in it is the same. In development that alone broke the entrance:
 * StrictMode double-invokes effects, so the loader ran twice, and the second identical response
 * restarted the clock about a second in — the animation visibly starting over. The same thing
 * happens in production wherever a screen re-fetches.
 *
 * Summarising the rows to a string means two identical loads produce the same key and the run is
 * left alone, while a new project, a new run or an edit produces a different one and replays it.
 * Pass the fields that actually decide what is drawn.
 */
export function contentKey<T>(items: T[] | null, of: (item: T) => string): string | null {
  return items === null ? null : items.map(of).join("|");
}

/** How much of the run each item waits out before it starts. */
const STAGGER_STEP = 0.07;
/** No item waits longer than this, or the last one in a long list arrives after the eye has moved on. */
const STAGGER_CAP = 0.55;

/**
 * One item's own eased progress within the shared run.
 *
 * The delay is what makes a row of tiles read as arriving in sequence rather than as one block
 * changing at once, and it is what lets a chart be held back until the ones beside it have started.
 */
export function staggered(t: number, index: number): number {
  const delay = Math.min(index * STAGGER_STEP, STAGGER_CAP);
  return ease(Math.max(0, Math.min(1, (t - delay) / (1 - delay))));
}

/** A figure, counted up. `null` is "nothing to count" and stays a dash throughout. */
export function counted(
  value: number | null,
  progress: number,
  format: (n: number) => string = (n) => String(Math.round(n)),
): string {
  if (value === null) return "—";
  return format(value * progress);
}
